import PropTypes from 'prop-types'
import { useCallback, useState, useEffect, useRef } from 'react'
import {
  AdasBox,
  AdasButton,
  AdasButtonGroup,
  AdasCard,
  AdasInputAdornment,
  AdasTextField,
  AdasTypography,
  AdasFormControl,
  AdasInputLabel,
  AdasSelect
} from '@components/wrapper-components'
import DeleteOutlinedIcon from '@mui/icons-material/DeleteOutlined'
import { useLoadingStore } from '@caradasstore/LoadingStore'
import { removeCustomItem, submitCustomItem, getAllProductCategories, updateCustomItem } from '../../../api/api'
import { currencyInput, validations, formatInitialPrice } from '../../../utils/utils'
import { Logger } from '../../../logger'
import { CurrencyInput } from '@components/currency-input'
import { useCenterStore } from '@caradasstore/CenterStore'

const MAX_LENGTH_NAME = 100
const MAX_LENGTH_DESC = 250
const MAX_QTY = 999
const MAX_LABOR_QTY = 999.99
const SPACING = '20px'

export const CustomItemForm = ({
  customItemData = {
    name: '',
    price: '',
    description: '',
    quantity: 1,
    total_price: 0,
    category_id: '',
    type: 'CHARGE'
  },
  cancel,
  onSaveSuccess,
  workOrderData,
  isNew = false,
  multiline = true,
  type
}) => {
  const ccId = useCenterStore((store) => store.ccId)
  const setLoading = useLoadingStore((store) => store.setLoading)

  const [customItem, setCustomItem] = useState(customItemData)
  const [itemType, setItemType] = useState(customItemData.type || 'CHARGE')
  const [productCategories, setProductCategories] = useState([])
  const [touched, setTouched] = useState({ name: false, price: false })
  const [valid, setValid] = useState({
    name: validations.name(customItemData.name),
    price: validations.price(customItemData.price),
    quantity: Number(customItemData.quantity) >= 1
  })

  const isLabourCategory = useCallback(
    (categoryId) => {
      const selected = productCategories.find((cat) => String(cat.id) === String(categoryId))
      return selected?.type === 'LABOR'
    },
    [productCategories]
  )

  const handleInputChange = useCallback(
    (e) => {
      const name = e?.target?.name || e.key
      let value = e?.target?.value || e.value

      if (name === 'description') value = value.replace(/[\n\r]/g, '')
      if (name === 'price') value = currencyInput(value) / 100

      setCustomItem((prev) => {
        const updated = { ...prev, [name]: value }
        const quantity = parseFloat(updated.quantity) || 0
        const price = parseFloat(updated.price) || 0
        updated.total_price = quantity * price
        return updated
      })

      if (validations[name]) {
        setTouched((prev) => ({ ...prev, [name]: true }))
        setValid((prev) => ({ ...prev, [name]: validations[name](value) }))
      } else if (name === 'quantity') {
        const num = parseFloat(value)
        const isValidQty = isLabourCategory(customItem.category_id)
          ? !isNaN(num) && num >= 1 && num <= MAX_LABOR_QTY
          : !isNaN(num) && num >= 1 && num <= MAX_QTY

        setTouched((prev) => ({ ...prev, quantity: true }))
        setValid((prev) => ({ ...prev, quantity: isValidQty }))
      }
    },
    [customItem.category_id, isLabourCategory]
  )

  const handleLaborQuantityChange = (e) => {
    const value = e.target.value
    if (value === '') {
      handleInputChange({ key: 'quantity', value: '' })
      return
    }

    if (/^\d*\.?\d{0,2}$/.test(value)) {
      const numericValue = parseFloat(value)
      if (!isNaN(numericValue) && numericValue <= MAX_LABOR_QTY) {
        handleInputChange({ key: 'quantity', value })
      }
    }
  }

  const handleQuantityChange = (e) => {
    const value = e.target.value.replace(/[^0-9]/g, '').replace(/^0+/, '')
    if (!value) return handleInputChange({ key: 'quantity', value: '' })
    const numericValue = parseInt(value, 10)
    if (numericValue <= MAX_QTY) {
      handleInputChange({ key: 'quantity', value })
    }
  }

  const handleInputBlur = (e) => {
    if (e.target.name === 'quantity' && e.target.value.trim() === '') {
      setCustomItem((prev) => ({
        ...prev,
        quantity: 1,
        total_price: (Number(prev.price) || 0) * 1
      }))
      setTouched((prev) => ({ ...prev, quantity: true }))
      setValid((prev) => ({ ...prev, quantity: true }))
    }
  }

  const fetchProductCategories = useCallback(async () => {
    try {
      const res = await getAllProductCategories({ ccid: ccId })
      if (res.status === 200) {
        setProductCategories(res.data)

        if (res.data.length === 1 && type !== 'calibration') {
          handleInputChange({ key: 'category_id', value: res.data[0].id })
        }
      }
    } catch (error) {
      Logger.error({ message: error, payload: { file: 'ProductForm/fetchProductCategories' } })
    }
  }, [ccId, type])

  useEffect(() => {
    fetchProductCategories()
  }, [fetchProductCategories])

  const filteredCategories = productCategories.filter((cat) =>
    itemType === 'DISCOUNT' ? cat.name.toLowerCase() === 'discount' : cat.name.toLowerCase() !== 'discount'
  )

  useEffect(() => {
    if (!filteredCategories.some((cat) => cat.id === customItem.category_id) && filteredCategories.length > 0) {
      setCustomItem((prev) => ({ ...prev, category_id: filteredCategories[0].id }))
    }
  }, [itemType, filteredCategories])

  const prevCategoryIsLabor = useRef(isLabourCategory(customItem.category_id))
  useEffect(() => {
    const isNowLabor = isLabourCategory(customItem.category_id)

    // If it switched FROM labor to non-labor and is decimal, floor it
    if (!isNowLabor && prevCategoryIsLabor.current && customItem.quantity % 1 !== 0) {
      setCustomItem((prev) => ({ ...prev, quantity: Math.floor(prev.quantity) }))
    }

    // Update ref for next render
    prevCategoryIsLabor.current = isNowLabor
  }, [customItem.category_id, isLabourCategory])

  const handleSaveCustomItem = async () => {
    setLoading(true)
    const payload = {
      ...customItemData,
      ...customItem,
      workorder_id: workOrderData.id,
      calibration_center_id: workOrderData.calibrationCenter.id,
      type: itemType.toUpperCase()
    }

    try {
      const api = isNew ? submitCustomItem : updateCustomItem
      const res = await api({ type, data: payload, id: workOrderData.id })
      if (res?.status === 200 || res?.status === 201) onSaveSuccess()
    } catch (error) {
      Logger.error({ message: error, payload: { file: 'CustomItemForm/handleSaveCustomItem', payload } })
    } finally {
      setLoading(false)
    }
  }

  const deleteCustomItemHandler = async () => {
    try {
      const res = await removeCustomItem({ type, id: workOrderData.id, customItemId: customItemData.id })
      if (res.status === 200 || res.status === 201) onSaveSuccess()
    } catch (error) {
      Logger.error({
        message: error,
        payload: { file: 'CustomItemForm/deleteCustomItemHandler', data: { type, id: workOrderData.id, customItemId: customItemData.id } }
      })
    }
  }

  return (
    <AdasCard className='adas-card'>
      <AdasTypography variant='overline' sx={{ fontWeight: 500, display: 'block' }}>
        ITEM TYPE
      </AdasTypography>

      <AdasButtonGroup variant='contained'>
        {['CHARGE', 'DISCOUNT'].map((btn) => (
          <AdasButton key={btn} onClick={() => setItemType(btn)} variant={itemType === btn ? 'contained' : 'outlined'}>
            {btn}
          </AdasButton>
        ))}
      </AdasButtonGroup>

      <AdasTextField
        name='name'
        label='Name'
        id='name'
        variant='outlined'
        fullWidth
        value={customItem.name}
        onChange={handleInputChange}
        inputProps={{ maxLength: MAX_LENGTH_NAME }}
        InputLabelProps={{ shrink: true }}
        error={touched.name && !valid.name}
        helperText={`Maximum ${MAX_LENGTH_NAME} Characters`}
        sx={{ my: SPACING }}
      />

      <AdasFormControl fullWidth sx={{ mb: '1.5rem' }}>
        <AdasInputLabel id='category_label'>Category</AdasInputLabel>
        <AdasSelect
          labelId='Category'
          id='category'
          name='category_id'
          value={customItem.category_id}
          label='Category'
          onChange={handleInputChange}
          options={filteredCategories}
        />
      </AdasFormControl>

      <AdasTextField
        name='price'
        label='Unit Price'
        id='price'
        variant='outlined'
        fullWidth
        type='text'
        value={customItem.price?.toLocaleString('en-US', { minimumFractionDigits: 2 })}
        onChange={handleInputChange}
        onKeyDown={(e) => {
          if (!/^\d$/.test(e.key) && !['Backspace', 'Enter', 'Tab'].includes(e.key)) e.preventDefault()
        }}
        InputProps={{ startAdornment: <AdasInputAdornment position='start'>$</AdasInputAdornment> }}
        inputProps={{ inputMode: 'numeric', pattern: '[0-9]*' }}
        InputLabelProps={{ shrink: true }}
        error={touched.price && !valid.price}
        sx={{ mb: SPACING }}
      />

      <AdasTextField
        name='quantity'
        label='Quantity'
        id='quantity'
        variant='outlined'
        fullWidth
        value={customItem.quantity}
        onBlur={handleInputBlur}
        onChange={isLabourCategory(customItem.category_id) ? handleLaborQuantityChange : handleQuantityChange}
        error={touched.quantity && !valid.quantity}
        helperText={
          touched.quantity && !valid.quantity
            ? isLabourCategory(customItem.category_id)
              ? `Enter a number between 1 and ${MAX_LABOR_QTY}`
              : `Enter a whole number between 1 and ${MAX_QTY}`
            : ''
        }
        InputLabelProps={{ shrink: true }}
        sx={{ mb: SPACING }}
      />

      <CurrencyInput
        label='Total Price'
        name='totalPrice'
        id='totalPrice'
        value={formatInitialPrice(customItem.total_price)}
        fullWidth
        disabled
        variant='outlined'
        InputLabelProps={{ shrink: true }}
        sx={{ mb: SPACING }}
      />

      <AdasTextField
        name='description'
        label='Description'
        id='description'
        variant='outlined'
        fullWidth
        multiline={multiline}
        value={customItem.description}
        onChange={handleInputChange}
        inputProps={{ maxLength: MAX_LENGTH_DESC }}
        helperText={`Maximum ${MAX_LENGTH_DESC} Characters ${MAX_LENGTH_DESC - (customItem.description?.length || 0)} remaining`}
        InputLabelProps={{ shrink: true }}
        sx={{ mb: SPACING }}
      />

      <AdasBox sx={{ mt: SPACING }}>
        {!isNew && (
          <AdasButton startIcon={<DeleteOutlinedIcon />} onClick={deleteCustomItemHandler} isDebounceEnabled sx={{ color: 'red' }}>
            Delete
          </AdasButton>
        )}
        <AdasBox sx={{ float: 'right' }}>
          <AdasButton variant='outlined' onClick={cancel} sx={{ mr: '10px' }}>
            Cancel
          </AdasButton>
          <AdasButton
            variant='contained'
            disabled={!valid.name || !valid.price || !valid.quantity}
            onClick={handleSaveCustomItem}
            isDebounceEnabled
          >
            Save
          </AdasButton>
        </AdasBox>
      </AdasBox>
    </AdasCard>
  )
}

CustomItemForm.propTypes = {
  customItemData: PropTypes.object,
  cancel: PropTypes.func.isRequired,
  onSaveSuccess: PropTypes.func.isRequired,
  workOrderData: PropTypes.object.isRequired,
  isNew: PropTypes.bool,
  multiline: PropTypes.bool,
  type: PropTypes.string
}
